import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { NavigationService } from '@core/navigation/navigation.service';
import { Navigation } from '@core/navigation/navigation.types';
import { environment } from '@environments/environments';
import { MessagesService } from '@layout/common/messages/messages.service';
import { NotificationsService } from '@layout/common/notifications/notifications.service';
import { Notification } from '@layout/common/notifications/notifications.types';
import { QuickChatService } from '@layout/common/quick-chat/quick-chat.service';
import { Chat } from '@layout/common/quick-chat/quick-chat.types';
import { ShortcutsService } from '@layout/common/shortcuts/shortcuts.service';
import { Shortcut } from '@layout/common/shortcuts/shortcuts.types';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { WebsiteSettingsView } from '@shared/models/domain/domain-view.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { WebsiteSettingsViewService } from '@shared/services/settings.service';
import { forkJoin } from 'rxjs';

export const InitialDataResolver: ResolveFn<[Navigation, Notification[], Notification[], Chat[], Shortcut[]]> =
() => {
    const messagesService: MessagesService = inject(MessagesService);
    const navigationService: NavigationService = inject(NavigationService);
    const notificationsService: NotificationsService = inject(NotificationsService);
    const quickChatService: QuickChatService = inject(QuickChatService);
    const shortcutsService: ShortcutsService = inject(ShortcutsService);
    return forkJoin([
        navigationService.get(),
        messagesService.getAll(),
        notificationsService.getAll(),
        quickChatService.getChats(),
        shortcutsService.getAll()
    ]);
};

export const OfficeDataResolver: ResolveFn<[Navigation, Notification[], Notification[], Chat[], Shortcut[]]> =
() => {
    const messagesService: MessagesService = inject(MessagesService);
    const navigationService: NavigationService = inject(NavigationService);
    const notificationsService: NotificationsService = inject(NotificationsService);
    const quickChatService: QuickChatService = inject(QuickChatService);
    const shortcutsService: ShortcutsService = inject(ShortcutsService);
    return forkJoin([
        navigationService.getOfficeNavigation(),
        messagesService.getAll(),
        notificationsService.getAll(),
        quickChatService.getChats(),
        shortcutsService.getAll()
    ]);
};

export const DriverDataResolver: ResolveFn<[Navigation, Notification[], Notification[], Chat[], Shortcut[]]> =
() => {
    const messagesService: MessagesService = inject(MessagesService);
    const navigationService: NavigationService = inject(NavigationService);
    const notificationsService: NotificationsService = inject(NotificationsService);
    const quickChatService: QuickChatService = inject(QuickChatService);
    const shortcutsService: ShortcutsService = inject(ShortcutsService);
    return forkJoin([
        navigationService.getDriverNavigation(),
        messagesService.getAll(),
        notificationsService.getAll(),
        quickChatService.getChats(),
        shortcutsService.getAll()
    ]);
};

export const WebsiteSettingsDataResolver: ResolveFn<PageResponse<WebsiteSettingsView>> =
(route: ActivatedRouteSnapshot) => {
    const websiteSettingsService = inject(WebsiteSettingsViewService);
    const officeTenantsService = inject(OfficeTenantsService);
    const { token, source, slug } = route.queryParams ?? {};
    let websiteId = officeTenantsService.getTenantDetailsBySlug()?.tenantId;
    if (token && source && slug) websiteId = officeTenantsService.getAllTenantsViewItems()?.find(website => website.slug === slug)?.tenantId;
    return websiteSettingsService.getWebsiteSettings(websiteId || environment.defaultWebsiteId);
}
